import React from "react";
import {StyledBoxColumn} from "../shared/StyledComponents";
import {nightGray, white} from "../shared/colors";
import {Button, Card, Typography} from "@mui/material";
import {PAGE_FRAGMENT_WIDTH, PD_MD} from "../shared/dimens";
import {App} from "./App";
import {checkPermissions, Permission, PermissionsFragment} from "../shared/PermissionsFragment";
import {BuzzButton} from "./Components";
import {Action} from "../shared/types";
import {
  BaseMeetingFragment,
  BaseMeetingFragmentProps,
  BaseMeetingFragmentState,
  PermissionsState
} from "./BaseMeetingFragment";
import {MeetingFragment, MeetingFragmentListener} from "./MeetingFragment";
import {Room} from "./types";

export type RoomLobbyFragmentProps = BaseMeetingFragmentProps & {
  initialRoom: Room,
}

type RoomLobbyFragmentState = BaseMeetingFragmentState & {
  room?: Room,
  mediaStream?: MediaStream,
  started?: boolean,
}

export class RoomLobbyFragment extends BaseMeetingFragment<RoomLobbyFragmentProps, RoomLobbyFragmentState> implements MeetingFragmentListener {

  private readonly appRefs = App.getAppRefs();

  private readonly permissions = [Permission.CAMERA_AND_MICROPHONE];

  protected onCreateState(): RoomLobbyFragmentState {
    return {
      ...super.onCreateState(),
      room: this.props.initialRoom,
    };
  }

  protected async fetchOnMount(): Promise<void> {
    const result = await checkPermissions(this.permissions);
    if (result.deniedPermissions.length === 0 && result.promptPermissions.length === 0) {
      this.setState({
        permissionsState: PermissionsState.GRANTED,
      });
    }
  }

  componentWillUnmount() {
    this.state.mediaStream?.getTracks().forEach(track => track.stop());
    if (this.appRefs.localVideoRenderer.current) {
      this.appRefs.localVideoRenderer.current.srcObject = null;
    }
    this.setState({
      mediaStream: null,
    });
  }

  componentDidUpdate(prevProps: Readonly<RoomLobbyFragmentProps>, prevState: Readonly<RoomLobbyFragmentState>, snapshot?: any) {
    if (prevState.ready !== this.state.ready && this.state.permissionsState === PermissionsState.GRANTED) {
      navigator.mediaDevices.getUserMedia({video: true, audio: true})
        .then(mediaStream => this.setState({
          mediaStream: mediaStream,
        }));
    }
    if (!this.state.started) {
      if (this.state.mediaStream !== prevState.mediaStream || prevState.started) {
        if (this.state.mediaStream) {
          this.appRefs.localVideoRenderer.current.srcObject = this.state.mediaStream;
        }
      }
    }
  }

  renderContent(): React.ReactElement {
    return <StyledBoxColumn style={{
      boxSizing: "border-box",
      flexGrow: 1,
      width: "100%",
      maxWidth: PAGE_FRAGMENT_WIDTH,
      padding: PD_MD,
      margin: "auto",
      justifyContent: "center"
    }}>
      <Card style={{
        width: "100%",
        background: nightGray,
        aspectRatio: 16 / 9,
        position: "relative",
      }}>
        {!this.state.started && this.appRefs.localVideoElement}
        {!this.state.started && this.appRefs.localAudioElement}
        {this.state.permissionsState !== PermissionsState.GRANTED
          ? <StyledBoxColumn style={{
            position: "absolute",
            alignItems: "center",
            justifyContent: "center",
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
          }}>
            <Typography style={{color: white}}>Additional permissions required.</Typography>
            <Button variant="outlined" color="secondary" onClick={() => App.CONTEXT.showDialog(null, () => {
              return <PermissionsFragment
                permissions={this.permissions}
                promptTitle="Permissions"
                promptText="Before we can start a call, we need some permissions from you."
                deniedTitle="Video chat"
                deniedText="We cannot start this call because you denied permissions."
                onGranted={() => {
                  this.setState({permissionsState: PermissionsState.GRANTED})
                }}
                onDenied={() => {
                  // this.setState({permissionsState: PermissionsState.DENIED});
                  // this.meetings.endCurrentMeeting(this.props.auth, MeetingEndReason.NO_PERMISSIONS);
                }}
                onDismiss={() => {
                  // this.meetings.clearCurrentMeeting();
                }}
              />
            })}>Grant</Button>
          </StyledBoxColumn>
          : null}
      </Card>
      <BuzzButton action={new Action("Start", () => {
        this.setState({
          started: true,
        });
        App.CONTEXT.showFullscreenDialog(() => <MeetingFragment room={this.state.room} listener={this}/>);
      })}/>
    </StyledBoxColumn>
  }

  onMinimizedStateChanged(minimized: boolean) {
  }

  onMeetingEnded() {
    this.setState({
      started: false,
    });
    App.CONTEXT.hideDialog();
  }
}